import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Stock,
  CreateStockRequest,
  UpdateStockRequest,
  MarkReadyRequest,
  BulkReadinessRequest,
  BulkReadinessResponse,
  DeleteStocksRequest,
  DeleteStocksResponse,
  GetStocksRequest,
  GetStocksResponse,
  GetTransferRunsRequest,
  GetTransferRunsResponse,
  DailyReportResponse,
} from './stock.type';
import { environment } from '../../environments/environment';

/** The backend wraps every success as {result: ...}. */
interface Envelope<T> { result: T; }

@Injectable({ providedIn: 'root' })
export class StockService {
  private apiUrl = environment.apiBaseURL + '/stocks';

  constructor(private http: HttpClient) { }

  getStocks(request: GetStocksRequest = {}): Observable<GetStocksResponse> {
    let params = new HttpParams();
    if (request.pageIndex !== undefined) params = params.set('pageIndex', request.pageIndex);
    if (request.pageSize !== undefined) params = params.set('pageSize', request.pageSize);
    if (request.supplierId) params = params.set('supplierId', request.supplierId);
    if (request.status) params = params.set('status', request.status);
    if (request.search) params = params.set('search', request.search);
    return this.http
      .get<Envelope<GetStocksResponse>>(this.apiUrl, { params })
      .pipe(map(r => r.result));
  }

  getStock(id: string): Observable<Stock> {
    return this.http.get<Envelope<Stock>>(`${this.apiUrl}/${id}`).pipe(map(r => r.result));
  }

  createStock(stock: CreateStockRequest): Observable<Stock> {
    return this.http.post<Envelope<Stock>>(this.apiUrl, stock).pipe(map(r => r.result));
  }

  updateStock(id: string, stock: UpdateStockRequest): Observable<Stock> {
    return this.http.put<Envelope<Stock>>(`${this.apiUrl}/${id}`, stock).pipe(map(r => r.result));
  }

  markReady(id: string, request: MarkReadyRequest): Observable<Stock> {
    return this.http
      .post<Envelope<Stock>>(`${this.apiUrl}/${id}/ready`, request)
      .pipe(map(r => r.result));
  }

  markNotReady(id: string): Observable<Stock> {
    return this.http
      .post<Envelope<Stock>>(`${this.apiUrl}/${id}/not-ready`, {})
      .pipe(map(r => r.result));
  }

  bulkMarkReady(request: BulkReadinessRequest): Observable<BulkReadinessResponse> {
    return this.http
      .post<Envelope<BulkReadinessResponse>>(`${this.apiUrl}/bulk/ready`, request)
      .pipe(map(r => r.result));
  }

  bulkMarkNotReady(ids: string[]): Observable<BulkReadinessResponse> {
    return this.http
      .post<Envelope<BulkReadinessResponse>>(`${this.apiUrl}/bulk/not-ready`, { ids, coins_to_be_remain: 0 })
      .pipe(map(r => r.result));
  }

  deleteStocks(ids: string[]): Observable<DeleteStocksResponse> {
    return this.http
      .delete<Envelope<DeleteStocksResponse>>(this.apiUrl, { body: { ids } as DeleteStocksRequest })
      .pipe(map(r => r.result));
  }

  // ── Transfer runs & reports ──

  getTransferRuns(request: GetTransferRunsRequest = {}): Observable<GetTransferRunsResponse> {
    let params = new HttpParams();
    for (const [k, v] of Object.entries(request)) {
      if (v !== undefined && v !== null && v !== '') params = params.set(k, String(v));
    }
    return this.http
      .get<Envelope<GetTransferRunsResponse>>(`${this.apiUrl}/transfer-runs`, { params })
      .pipe(map(r => r.result));
  }

  getDailyReport(request: GetTransferRunsRequest = {}): Observable<DailyReportResponse> {
    let params = new HttpParams();
    for (const [k, v] of Object.entries(request)) {
      if (v !== undefined && v !== null && v !== '') params = params.set(k, String(v));
    }
    return this.http
      .get<Envelope<DailyReportResponse>>(`${this.apiUrl}/reports/daily`, { params })
      .pipe(map(r => r.result));
  }
}
